import { UploadOutlined } from '@ant-design/icons';
import { Form, Input } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  type ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { UpdateClientPayload } from '@app/api/Client';
import {
  useLazyGetClientByIdQuery,
  useUpdateClientMutation,
} from '@app/api/Client/api';
import {
  type ModelClientCheckResult,
  useCheckClientForDuplicateMutation,
  useMergeClientsAtSameBranchMutation,
} from '@app/api/ClientsMerge';
import { useLazyGetClientLanguagesQuery } from '@app/api/Dictionaries';
import { useNormalizeUserPhotoMutation } from '@app/api/User/api';
import type { PhoneValidation } from '@app/components/Phone/types';
import { DATE_FORMATS } from '@app/constants';
import { ERROR, SUCCESS, WARNING } from '@app/constants/alerts';
import { UNKNOWN_GENDER_ID } from '@app/constants/gender';
import { CALENDAR_PATH } from '@app/constants/navigation';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { LeavePageContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import { usePageContext } from '@app/providers/PageProvider/usePageContext';
import { handleCatchError } from '@app/services';
import { useLazyGetAllTagsQuery } from '@app/services/rtkQuery/Tags/api';
import { selectActiveBranchCountryId, useAppSelector } from '@app/store';
import type {
  NClients,
  NDictionaries,
  NSmallGlobalEntities,
  NTags,
} from '@app/types';

import type { BaseOptionType } from 'antd/es/select';
import type { InputRef, MenuProps } from 'antd/lib';

/* eslint-disable @typescript-eslint/no-explicit-any */

const { useForm } = Form;

export const useAddClient = () => {
  const { showNotification } = useNotification();
  const { setTitleData } = usePageContext();
  const activeBranchCountry = useAppSelector(selectActiveBranchCountryId);
  const {
    activeBranchId,
    params: { ActiveBranchId },
  } = useUserPrint();

  const [form] = useForm<NClients.AddClientFormValues>();

  const [leadId, setLeadId] = useState<number | null>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const clientId = Number(searchParams.get('id'));
  const [data, setData] = useState<any>(null);
  const [initialLoadData, setInitialLoadData] = useState<any>();
  const [isLoader, setLoader] = useState(false);
  const [duplicateCheckResult, setDuplicateCheckResult] =
    useState<ModelClientCheckResult | null>(null);
  const [phonesValidation, setPhonesValidation] = useState<{
    phone1: PhoneValidation;
    phone2: PhoneValidation;
  }>({
    phone1: { isValid: false, errorMessage: null },
    phone2: { isValid: false, errorMessage: null },
  });
  const [photo, setPhoto] = useState<string | null>(null);
  const [serverError, setServerError] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [languages, setLanguages] = useState<NDictionaries.ModelLanguage[]>([]);
  const [tags, setTags] = useState<NTags.Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<BaseOptionType[]>([]);

  const [checkClientForDuplicate] = useCheckClientForDuplicateMutation();
  const [mergeClientsAtSameBranch] = useMergeClientsAtSameBranchMutation();
  const [getClientById] = useLazyGetClientByIdQuery();
  const [getAllTags] = useLazyGetAllTagsQuery();
  const [updateClient] = useUpdateClientMutation();
  const [getClientLanguages] = useLazyGetClientLanguagesQuery();
  const [normalizeUserPhoto] = useNormalizeUserPhotoMutation();

  const {
    userAccessRights: {
      Clients: { IsTagsChange },
    },
  } = useNewUserAccessRights();

  const phoneInputRef = useRef<InputRef | null>(null);

  const { checkIsDataUpdated } = useContext(LeavePageContext);

  const handleInitialData = useCallback(
    (data: NClients.ModelClient) => {
      const commonData = {
        ...data,
        DateBirth: moment(data.DateBirth).format('YYYY-MM-DD'),
      };
      setData(commonData);

      form.setFieldsValue({
        ...commonData,
        Phone1: {
          Phone: data.Phone1 ?? '',
          CountryId: data.Phone1
            ? data.Phone1CountryId || 1
            : activeBranchCountry || 1,
        },
        Phone2: {
          Phone: data.Phone2 ?? '',
          CountryId: data.Phone2
            ? data.Phone2CountryId || 1
            : activeBranchCountry || 1,
        },
        NameLast: data?.NameLast || '',
        NameFirst: data?.NameFirst || '',
        NameMiddle: data?.NameMiddle || '',
        Email: data?.Email || '',
        Gender: (data?.Gender ||
          UNKNOWN_GENDER_ID) as NSmallGlobalEntities.Gender,
        Language: data?.LanguageId?.toString() || null,
        Tags: (data?.ClientTags?.filter(({ IsSelected }) => IsSelected) ||
          []) as Array<NTags.Tag | undefined>,
        DateBirth:
          clientId && data?.DateBirth && dayjs(data?.DateBirth).isValid()
            ? dayjs(data?.DateBirth, 'YYYY-MM-DD')
            : null,
      });

      setInitialLoadData(commonData);
    },
    [activeBranchCountry, clientId, form]
  );

  const fetchData = useCallback(async () => {
    if (clientId) {
      setLoader(true);
      try {
        const data = await getClientById({
          clientId,
          ActiveBranchId,
        }).unwrap();

        setLeadId(data?.LeadId || null);

        setTitleData(`${data.NameLast || ''} ${data.NameFirst || ''}`.trim());

        handleInitialData(data);

        const Phone1 = data.Phone1
          ? {
              CountryId: data.Phone1CountryId || 1,
              Phone: data.Phone1,
            }
          : {
              CountryId: activeBranchCountry || 1,
              Phone: '',
            };

        const Phone2 = data.Phone2
          ? {
              CountryId: data.Phone2CountryId || 1,
              Phone: data.Phone2,
            }
          : {
              CountryId: activeBranchCountry || 1,
              Phone: '',
            };

        form.setFieldsValue({
          Phone1,
          Phone2,
        });
        setPhoto(data.Photo);

        form.setFieldsValue({
          IsPhoneVerified: data.IsPhoneVerified,
          Language: data.LanguageId?.toString() || null,
          Comment: data.Comment,
        });

        const initialTags = data.ClientTags?.filter(
          (tag) => tag?.IsSelected
        ).map((tag) => ({
          value: tag?.Id,
        }));
        setSelectedTags(initialTags ? initialTags : []);

        if (data.IsPhoneVerified) {
          data.Phone1 && setVerifiedPhone(data.Phone1);
        }
      } catch (error) {
        showNotification(error as Error);
      }
    }
    try {
      const languagesResult = await getClientLanguages({
        ActiveBranchId,
      }).unwrap();
      setLanguages(languagesResult);
    } catch (error) {
      showNotification(error as Error);
    }

    try {
      const result = await getAllTags({
        ActiveBranchId: activeBranchId,
      }).unwrap();
      setTags(result);
    } catch (error) {
      showNotification(
        (error as Error).message,
        (error as Error).cause as NNotificationProvider.NotifyType
      );
    }
    setLoader(false);
  }, [
    clientId,
    getClientById,
    ActiveBranchId,
    setTitleData,
    handleInitialData,
    activeBranchCountry,
    form,
    showNotification,
    activeBranchId,
    getAllTags,
    getClientLanguages,
  ]);

  const skipIsPhoneVerified = useCallback(() => {
    if (
      data?.IsPhoneVerified &&
      verifiedPhone !== form?.getFieldValue('Phone1')
    ) {
      form?.setFieldValue('IsPhoneVerified', false);
    }
  }, [verifiedPhone, data?.IsPhoneVerified, form]);

  const sendDataToDb = useCallback(
    async ({
      NameFirst,
      NameLast = null,
      NameMiddle = null,
      Email = null,
      Gender = null,
      DateBirth = null,
      IsPhoneVerified = false,
      LanguageId = null,
      Comment = '',
      Phone1,
      Phone2,
      Snils,
      IdentityDocumentType,
      IdentityDocumentNumber,
      RegistrationAddress,
    }: NClients.AddClientFormValues) => {
      const { phone1, phone2 } = phonesValidation;
      const mainPhoneError =
        phone1.errorMessage || (!phone1.isValid ? 'Обязательное поле' : '');

      if (mainPhoneError) {
        form.setFields([
          {
            name: 'Phone1',
            errors: [mainPhoneError],
          },
        ]);
        return;
      }

      const handledClientTags = selectedTags
        .filter(({ value }) => {
          const originalTag = tags.find(({ Id }) => Id === value);

          if (originalTag !== undefined) return true;
        })
        .map(({ value }) => {
          const originalTag = tags.find(
            ({ Id }) => Id === value
          ) as NTags.ModelTag;

          return {
            ...originalTag,
            IsSelected: true,
          };
        });

      const isValidPhone2 = phone2.isValid && !phone2.errorMessage;
      const handledData: UpdateClientPayload = {
        Id: form.getFieldValue('Id'),
        NameFirst,
        NameLast,
        NameMiddle,
        Email,
        IsPhoneVerified,
        LanguageId,
        Gender,
        Phone1: Phone1.Phone,
        Phone1CountryId: Phone1.CountryId,
        Phone2: isValidPhone2 ? Phone2.Phone : null,
        Phone2CountryId: isValidPhone2 ? Phone2.CountryId : null,
        DateBirth: DateBirth?.isValid()
          ? DateBirth?.format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE)
          : null,
        Photo: photo,
        ClientTags: handledClientTags,
        Comment,
        IsHuman: true,
        Snils,
        IdentityDocumentType,
        IdentityDocumentNumber,
        RegistrationAddress,
      };

      setLoader(true);
      setIsSaving(true);

      try {
        const checkResult = await checkClientForDuplicate({
          ActiveBranchId,
          data: {
            ClientId: clientId || 0,
            ClientPhone: Phone1.Phone,
          },
        }).unwrap();

        if (checkResult.IsDuplicated) {
          setLoader(false);
          setIsSaving(false);
          setDuplicateCheckResult(checkResult);
          return;
        }
      } catch (error) {
        showNotification(error);
        setLoader(false);
        setIsSaving(false);
        return;
      }

      try {
        const data = await updateClient({
          ActiveBranchId: activeBranchId,
          data: handledData,
        }).unwrap();

        const leadId = Number(searchParams.get('leadId'));
        const leadIdParam = leadId ? `leadId=${leadId}` : '';
        leadIdParam
          ? navigate(`/leads/card?${leadIdParam}`)
          : navigate(`/clients/card/?id=${data.Id}`);

        showNotification('Данные клиента успешно сохранены', SUCCESS);
      } catch (error) {
        if ((error as Error).cause === ERROR) {
          showNotification(
            (error as Error).message,
            (error as Error).cause as NNotificationProvider.NotifyType
          );
        } else if ((error as Error).cause === WARNING) {
          setServerError((error as Error).message);
        }
      } finally {
        setLoader(false);
        setIsSaving(false);
      }
    },
    [
      phonesValidation,
      selectedTags,
      form,
      photo,
      tags,
      checkClientForDuplicate,
      ActiveBranchId,
      clientId,
      showNotification,
      updateClient,
      activeBranchId,
      searchParams,
      navigate,
    ]
  );

  const closeDuplicateModalHandler = useCallback(() => {
    setDuplicateCheckResult(null);
  }, []);

  const openExistingClientHandler = useCallback(() => {
    if (!duplicateCheckResult) return;
    navigate(`/clients/card/?id=${duplicateCheckResult.ExistingClientId}`);
    setDuplicateCheckResult(null);
  }, [duplicateCheckResult, navigate]);

  const mergeClientsHandler = useCallback(async () => {
    if (!duplicateCheckResult || !clientId) return;
    try {
      const { LeadId } = await mergeClientsAtSameBranch({
        ActiveBranchId,
        data: {
          ClientId1: clientId,
          ClientId2: duplicateCheckResult.ExistingClientId,
        },
      }).unwrap();
      setDuplicateCheckResult(null);
      showNotification('Карточки клиентов объединены', SUCCESS);
      navigate(`/leads/card?leadId=${LeadId}`);
    } catch (error) {
      showNotification(error);
    }
  }, [
    duplicateCheckResult,
    clientId,
    ActiveBranchId,
    mergeClientsAtSameBranch,
    showNotification,
    navigate,
  ]);

  const isEditMode = useMemo(() => {
    return !!clientId;
  }, [clientId]);

  const uploadAvatar = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files && event.target.files[0];

      if (file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async () => {
          if (typeof reader.result === 'string') {
            const base64String = reader.result
              ?.replace('data:', '')
              .replace(/^.+,/, '');
            try {
              const result = await normalizeUserPhoto({
                ActiveBranchId: activeBranchId,
                data: {
                  InputImage: base64String,
                  InputBytes: null,
                  NormalizedPhoto: null,
                },
              }).unwrap();
              setPhoto(result.NormalizedPhoto);
              showNotification('Фото установлено', SUCCESS);
            } catch (error) {
              showNotification(handleCatchError(error), WARNING);
            }
          }
        };
      }
    },
    [activeBranchId, showNotification, normalizeUserPhoto]
  );

  const items: MenuProps['items'] = useMemo(
    () => [
      {
        key: '1',
        label: (
          <div
            onClick={() => phoneInputRef.current?.input?.click()}
            className="unselectable"
          >
            <UploadOutlined style={{ marginRight: '10px' }} />
            Установить фото
            <Input
              ref={phoneInputRef}
              type="file"
              style={{ display: 'none' }}
              id="PhotoInput"
              onChange={uploadAvatar}
            />
          </div>
        ),
      },
    ],
    [uploadAvatar]
  );

  const handleCancel = useCallback(() => {
    if (window.location.pathname !== CALENDAR_PATH) {
      navigate('/clients');
    }
  }, [navigate]);

  const initSelectedTags = useCallback(() => {
    form.setFieldValue('Tags', selectedTags);
  }, [form, selectedTags]);

  useEffect(() => {
    return () => setTitleData('');
  }, [setTitleData]);

  return {
    form,
    data,
    clientId,
    isEditMode,
    setData,
    sendDataToDb,
    items,
    photo,
    serverError,
    setServerError,
    setVerifiedPhone,
    initialLoadData,
    isSaving,
    handleCancel,
    languages,
    tags,
    selectedTags,
    setSelectedTags,
    fetchData,
    skipIsPhoneVerified,
    initSelectedTags,
    isLoader,
    setPhonesValidation,
    checkIsDataUpdated,
    activeBranchCountry,
    phonesValidation,
    IsTagsChange,
    duplicateCheckResult,
    closeDuplicateModalHandler,
    openExistingClientHandler,
    mergeClientsHandler,
    leadId,
  };
};
